import { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import educationActions from '../../Actions/educationActions';
import useVonAuthentication from '../../Hooks/useVonAuthentication';

import VonAuthorization from '../../Components/VonAuthorization/VonAuthorization';
import Card from '../../Components/Card/Card';
import Grid from '../../Components/Grid/Grid';
import Spinner from '../../Components/Fundamentals/Spinner/Spinner';
import { FormInput } from '../../Components/FormComponents/Input/Input';
import { Checkbox } from '../../Components/FormComponents/Checkbox';
import customerRoutes from '../../Constants/Customer/customerRoutes';

const EducationCodeContainer = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isSignedIn = useVonAuthentication();
    const actions = bindActionCreators(educationActions, dispatch);

    const educationLicenseCode = useSelector((state) => state.CustomerReducer.customer?.vonCustomer?.educationLicenseId || '');
    const customerId = useSelector((state) => state.CustomerReducer.customer?.vonCustomer?.id);
    const customerPathId = useSelector((state) => state.CustomerReducer.customer?.pathId);

    const [educationServices, setEducationServices] = useState([]);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [initialSelectedIds, setInitialSelectedIds] = useState([]);

    const isReduxLoading = useSelector((state) => state.CommonReducer.isLoading);
    const [isEducationListLoading, setIsEducationListLoading] = useState(false);
    const isLoading = isReduxLoading || isEducationListLoading;

    const areArraysEqual = (array1, array2) => {
        if (array1.length !== array2.length) return false;

        const sorted1 = [...array1].sort();
        const sorted2 = [...array2].sort();

        return sorted1.every((val, index) => val === sorted2[index]);
    };

    const stateHasChanged = !areArraysEqual(initialSelectedIds, selectedServiceIds);
    const submitIsDisabled = isLoading || selectedServiceIds.length === 0 || !stateHasChanged;

    useEffect(() => {
        if (isSignedIn && customerId) {
            setIsEducationListLoading(true);

            actions.getEducationServices(customerId, (json) => {
                setEducationServices(json);

                const initialSelectedIds = (json ?? []).filter((service) => service.isActiveForCustomer).map((service) => service.id);
                setSelectedServiceIds(initialSelectedIds);
                setInitialSelectedIds(initialSelectedIds);

                setIsEducationListLoading(false);
            });
        }
    }, []);

    const handleCheckboxChange = (serviceId, isChecked) => {
        setSelectedServiceIds((prevSelectedIds) => {
            if (isChecked) {
                return [...prevSelectedIds, serviceId];
            } else {
                return prevSelectedIds.filter((id) => id !== serviceId);
            }
        });
    };

    const handleSubmit = () => {
        if (selectedServiceIds.length > 0) {
            actions.createEducationCode(customerId, selectedServiceIds, () => {
                setInitialSelectedIds([...selectedServiceIds]);
            });
        }
    };

    return (
        <Grid>
            <Grid.Row>
                <Grid.Row.Column width={4}>
                    <Card>
                        <Card.Header>
                            <Card.Header.Title>{t('educations.title')}</Card.Header.Title>
                        </Card.Header>
                        <Card.Content isLoading={isReduxLoading}>
                            <div style={{ position: 'relative', minHeight: '240px' }}>
                                <VonAuthorization redirectUrl={customerRoutes.customer(customerPathId, customerRoutes.EducationCode)}>
                                    <div className="form-horizontal">
                                        <fieldset className="col-xs-12">
                                            <div className="bg-default py-16 px-32 rounded-8 shadow-10 mb-24">
                                                <p className="m-0">{t('educations.description')}</p>
                                            </div>

                                            <div className="mb-32">
                                                <p className="bold mb-8">{t('educations.serviceListHeader')}</p>

                                                <ul className="p-0 border-top border-bottom no-bullet">
                                                    {isEducationListLoading ? (
                                                        <li className="py-8 px-16">
                                                            <Spinner />
                                                        </li>
                                                    ) : educationServices && educationServices.length > 0 ? (
                                                        educationServices.map((service, index) => (
                                                            <li className={`py-8 px-16 ${index % 2 === 1 ? 'bg-transparent' : 'bg-default'}`} key={service.id}>
                                                                <Checkbox
                                                                    label={service.name}
                                                                    onChange={(e) => handleCheckboxChange(service.id, e)}
                                                                    checked={selectedServiceIds.includes(service.id)}
                                                                />
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li className="py-8 px-16">{t('educations.servicesNotFound')}</li>
                                                    )}
                                                </ul>
                                            </div>

                                            <div>
                                                <p className="bold mb-8">{t('educations.submitHeader')}</p>
                                                <div className="d-flex align-items-center gap-16">
                                                    <button type="button" className="btn btn-primary btn-sm m-0 p-0" onClick={handleSubmit} disabled={submitIsDisabled}>
                                                        {t('educations.submitButton')}
                                                    </button>
                                                    <FormInput value={educationLicenseCode || ''} readOnly={true} />
                                                </div>
                                            </div>
                                        </fieldset>
                                    </div>
                                </VonAuthorization>
                            </div>
                        </Card.Content>
                    </Card>
                </Grid.Row.Column>
            </Grid.Row>
        </Grid>
    );
};

export default EducationCodeContainer;
